import { useMutation } from '@tanstack/react-query';

import {
  deleteV1NotificationsById,
  putV1NotificationsByIdStatus,
  putV1NotificationsStatusRead,
  type BulkStatusUpdateResult,
  type Status,
} from '../../api/generated/notification-inapp';
import { queryClient } from '../../api/queryClient';

/**
 * Mutation hooks for the in-app Notifications feature.
 *
 * Wrap the generated `@hey-api` SDK calls (with `throwOnError`) and invalidate
 * the active notifications list and the unread count on success and on error.
 * The `If-Match` header for mutating requests is attached automatically by the
 * shared request interceptor (`src/api/clients/interceptors.ts`).
 */

const invalidateActiveNotificationsList = () => {
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === 'notifications',
    refetchType: 'active',
  });
};

export function useUpdateNotificationStatus() {
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Status }) => {
      const { data } = await putV1NotificationsByIdStatus({
        path: { id },
        body: status,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: invalidateActiveNotificationsList,
  });
}

export function useDeleteNotification() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await deleteV1NotificationsById({ path: { id }, throwOnError: true });
      return data;
    },
    onSuccess: invalidateActiveNotificationsList,
  });
}

/**
 * The backend caps each `PUT /v1/notifications/status-read` call at this many
 * notifications: a batch that reports exactly the cap leaves the loop open, a
 * partial batch closes it.
 */
const MARK_ALL_AS_READ_BATCH_LIMIT = 1000;

/**
 * Marks all of the user's notifications read by repeating one-batch calls.
 *
 * The endpoint takes no filter parameters and marks every unread notification
 * of the recipient. It flips unread notifications to read only, so a retry
 * after a partial failure processes the remainder without side effects.
 *
 * @param markBatchAsRead - Performs a single batch call and returns its result.
 * @returns The result of the final (partial) batch.
 */
export async function markAllAsReadInBatches(
  markBatchAsRead: () => Promise<BulkStatusUpdateResult>
): Promise<BulkStatusUpdateResult> {
  let result: BulkStatusUpdateResult;
  do {
    result = await markBatchAsRead();
  } while (result.updated >= MARK_ALL_AS_READ_BATCH_LIMIT);
  return result;
}

export function useMarkAllAsRead() {
  return useMutation({
    mutationFn: () =>
      markAllAsReadInBatches(async () => {
        const { data } = await putV1NotificationsStatusRead({ throwOnError: true });
        return data;
      }),
    onSuccess: invalidateActiveNotificationsList,
    onError: invalidateActiveNotificationsList,
  });
}
